import React from 'react';
import { Typography } from '@mui/material';
import ExperianChallengeRut from './00_rut';
import ExperianChallengeForm from './01_form';
import { FormSchemaV3, PLRStepV3 } from '../../../../../../../../types/preloan/v3';
import AlertDialog from '../../../../../../../../components/AlertDialog';
import BancameButton from '../../../../../../../../components/BancameButton';
import { ExperianChallengeProps, ExperianFormProps } from './types';
import ExperianChallengeResult from './02_result';
import AgentContactButton from '../../../../../../../../components/AgentContactButton';
import { handleDataOK, handlePLRstateSwitch, returnDataForm } from '../../../02_dataFetching/components/helpers';
import { patchFillForm } from '../../../../../../../../queries/preloan/v3';
import analytics from '../../../../../../../../utils/analytics';
import experianLogo from '../../../../../../../../assets/logo-experian.svg';

export const experianErrorSwitch = (errorCode: string) => {
  switch (errorCode) {
    case '10006':
      return 'Ha ocurrido un problema generando el desafío de Experian. Por favor reintenta. Si el problema persiste, contáctanos.';
    case '10011':
      return 'Se ha llegado al máximo de intentos permitidos, por favor vuelve más tarde (Este bloqueo puede durar hasta 72 horas).';
    case '10008':
      return 'La cédula de identidad no se encuentra vigente.';
    case '10001':
      return 'Ha ocurrido un error verificando tu cédula. Por favor inténtalo de nuevo';
    case '10305':
      return 'Usted ya ha realizado el máximo de intentos para el ciclo de Desafío del Rut ingresado';
    default:
      return `Ha ocurrido un error (${errorCode}). Por favor vuelve más tarde. Si el error persiste contáctanos.`;
  }
};

export const termsAndConditions = `Por este acto, autorizo a mi AFP,
a entregar por intermedio de Previred, mis 12 últimos períodos de cotizaciones
previsionales a la Institución, con el fin de ser consideradas como antecedente
a esta solicitud comercial, dando así cumplimiento al artículo 4° de la ley N° 19.628
sobre Protección de la Vida Privada y a lo dispuesto en la ley N° 19.799
sobre Documentos
Electrónicos Firma Electrónica y Servicio de Certificación de Dicha Firma`;

const experianComponentSwitch = (props: ExperianChallengeProps
  & {step: number, refsObject: {
    rutComponentRef: React.MutableRefObject<any>,
    formComponentRef: React.MutableRefObject<any>}}) => {
  const {
    values,
    handleChange,
    setFieldValue,
    handleSetDisableContinue,
    loading,
    error,
    errorExternalInformation,
    step,
    handleSetStep,
    handleUnemployedFallback,
    patchManualVerificationFallback,
  } = props;
  switch (step) {
    case 0:
      return (
        <ExperianChallengeRut
          values={values}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          handleSetDisableContinue={handleSetDisableContinue}
          loading={loading}
          error={error}
          errorExternalInformation={errorExternalInformation}
          handleSetStep={handleSetStep}
          handleUnemployedFallback={handleUnemployedFallback}
          patchManualVerificationFallback={patchManualVerificationFallback}
        />
      );

    case 1:
      return (
        <ExperianChallengeForm
          values={values}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          handleSetDisableContinue={handleSetDisableContinue}
          loading={loading}
          error={error}
          errorExternalInformation={errorExternalInformation}
          handleSetStep={handleSetStep}
          handleUnemployedFallback={handleUnemployedFallback}
          patchManualVerificationFallback={patchManualVerificationFallback}
        />
      );
    case 2:
      return (
        <ExperianChallengeResult
          values={values}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          handleSetDisableContinue={handleSetDisableContinue}
          loading={loading}
          error={error}
          errorExternalInformation={errorExternalInformation}
          handleSetStep={handleSetStep}
          handleUnemployedFallback={handleUnemployedFallback}
          patchManualVerificationFallback={patchManualVerificationFallback}
        />
      );
    default:
      return <div />;
  }
};

const queryExperianCanalInstitucion = () => {
  if (window.location.href.includes('localhost') || window.location.href.includes('dev')) {
    return '00000';
  }
  return '00001';
};
const handleSubmit = async (props: {
  values: FormSchemaV3,
  step: number,
  handleSetError: (state: boolean) => void,
  handleSetLoading: (state: boolean)=> void,
  queriesArray: ExperianFormProps['queriesArray'],
  setFieldValue: ExperianFormProps['setFieldValue'],
  handleCurrentData: ExperianFormProps['handleCurrentData'],
  handleStep: ExperianFormProps['handleStep'],
  handleEnableLoading: ExperianFormProps['handleEnableLoading'],
  handleSetErrorExternalInformation: (state: boolean) => void,
}) => {
  const {
    values,
    step,
    handleSetError,
    handleSetLoading,
    queriesArray,
    setFieldValue,
    handleCurrentData,
    handleStep,
    handleEnableLoading,
    handleSetErrorExternalInformation,
  } = props;
  handleSetLoading(true);
  handleSetError(false);
  try {
    switch (step) {
      case 1: {
        const res = await queriesArray[0]({
          serialNumber: values.serialNumber,
          canalInstitucion: queryExperianCanalInstitucion(),
          token: values.PLRtoken,
        });
        setFieldValue('experianChallenge', res.data);
        if (res.data.errors.length > 0) {
          return handleSetError(true);
        }
        return handleSetLoading(false);
      }
      case 2: {
        const res = await queriesArray[1]({
          canalInstitucion: queryExperianCanalInstitucion(),
          challengeId: values.experianChallenge.data.RESUMEN.IDCHALLENGE,
          RESPUESTAS: Object.entries(values.experianChallengeResponses)
            .map(([key, value]) => ({ CODIGO_PREGUNTA: key, CODIGO_RESPUESTA: value })),
          token: values.PLRtoken,
        });
        setFieldValue('experianChallengeResult', res.data);
        handleSetLoading(false);
        if (res.data.errors.length > 0) {
          handleSetError(true);
        }
        if (res.data.success) {
          analytics.page('PLR - SITUACION LABORAL', 'EXITO CHALLENGE EXPERIAN');
          switch (res.data.plr.step) {
            case 'FILL_FORM': {
              const response = await patchFillForm(values.PLRtoken);
              const { dataForm, contactForm } = returnDataForm({
                rawDataForm: response.data.form,
                phone: response.data.plr.phone,
                email: response.data.plr.email,
                formSchema: values,
              });
              handleCurrentData({
                ...values, contactForm, dataForm, step: response.data.plr.step,
              });
              return handlePLRstateSwitch({
                plrState: response.data.plr.step,
                handleStep,
                handleSetDataLoading: handleSetLoading,
                handleEnableLoading,
              });
            }
            case 'UPLOAD_DOCUMENTS':
              handleCurrentData({
                ...values,
                step: res.data.plr.step,
                settlementsRequired: res.data.settlementsRequired,
                pensionStatementsRequired: res.data.pensionStatementsRequired,
              });
              return handlePLRstateSwitch({
                plrState: res.data.plr.step,
                handleStep,
                handleSetDataLoading: handleSetLoading,
                handleEnableLoading,
              });
            case 'ARCHIVED_FAIL':
              handleCurrentData({
                ...values,
                step: res.data.plr.step,
                adminComment: res.data.plr.adminComment,
                isRedirect: 'SITUACION LABORAL',
              });
              return handlePLRstateSwitch({
                plrState: res.data.plr.step,
                handleStep,
                handleSetDataLoading: handleSetLoading,
                handleEnableLoading,
              });
            case 'DATA_OK': {
              handleCurrentData({
                ...values, step: res.data.plr.step,
              });
              return handleDataOK({
                token: values.PLRtoken,
                newData: values,
                handleCurrentData,
                formSchema: values,
                handleSetPLRState: (newStep: PLRStepV3|'error'|false) => handlePLRstateSwitch({
                  plrState: newStep,
                  handleStep,
                  handleSetDataLoading: handleSetLoading,
                  handleEnableLoading,
                }),
              });
            }
            default:
              return handleSetError(true);
          }
        }
        return true;
      }
      default:
        return handleSetLoading(false);
    }
  } catch (e) {
    if (e === 509) {
      analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', {
        initiator: 'backend',
        errorCode: e,
      });
      handleSetErrorExternalInformation(true);
    } else {
      analytics.track('ERROR', {
        initiator: 'backend',
        errorCode: e,
      });
    }
    handleSetLoading(false);
    return handleSetError(true);
  }
};
export function getExperianComponent(
  props:ExperianFormProps,
) {
  const {
    values,
    handleChange,
    setFieldValue,
    queriesArray,
    handleCurrentData,
    handleStep,
    handleEnableLoading,
    handleUnemployedFallback,
    patchManualVerificationFallback,
  } = props;

  const [disableContinue, setDisableContinue] = React.useState(false);
  const handleSetDisableContinue = (state: boolean) => setDisableContinue(state);
  const [step, setStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorExternalInformation, setErrorExternalInformation] = React.useState(false);
  const handleSetLoading = (state: boolean) => setLoading(state);
  const handleSetError = (state: boolean) => setError(state);
  const handleSetErrorExternalInformation = (state: boolean) => setErrorExternalInformation(state);
  const handleSetStep = (state: number) => setStep(state);
  const rutComponentRef = React.useRef<any>(null);
  const formComponentRef = React.useRef<any>(null);

  React.useEffect(() => {
    handleSubmit({
      values,
      step,
      handleSetError,
      handleSetLoading,
      queriesArray,
      setFieldValue,
      handleCurrentData,
      handleStep,
      handleEnableLoading,
      handleSetErrorExternalInformation,
    });
  }, [step]);

  React.useEffect(() => {
    analytics.page(values.isRedirect ? `PLR - ${values.isRedirect}`
      : 'PLR - SITUACION LABORAL', 'CONSULTA PREVIRED');
    handleCurrentData({ ...values, isRedirect: false });
  }, []);

  return (
    <AlertDialog
      action={() => handleSetStep(step + 1)}
      dialogTitle={(
        <div style={{ textAlign: 'center' }}>
          <object data={experianLogo} aria-labelledby="Experian Logo" height="70px" />
        </div>
)}
      button={(
        <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
          <BancameButton
            label="Obtener Cotizaciones"
            type="form"
            displayFlex
          />
        </div>
)}
      submitButtonDisabled={disableContinue || loading || values.experianRetries > 2}
      closeButtonDisabled={loading}
      hideSubmitButton={step >= 2}
      hideCloseButton={values.experianChallengeResult.success}
      dialogContent={(
        <>
          {experianComponentSwitch({
            step,
            handleChange,
            handleSetDisableContinue,
            values,
            setFieldValue,
            loading,
            error,
            errorExternalInformation,
            refsObject: { rutComponentRef, formComponentRef },
            handleSetStep,
            handleUnemployedFallback,
            patchManualVerificationFallback,
          })}
          {values.experianRetries && values.experianRetries < 3
            ? (
              <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  style={{
                    backgroundColor: 'whitesmoke',
                    padding: 10,
                    margin: 5,
                  }}
                  borderRadius={20}
                  maxWidth={200}
                >
                  Reintentos:
                  {' '}
                  {values.experianRetries}
                  {' '}
                  de 2

                </Typography>
              </div>

            ) : null}
          {values.experianRetries > 2
            && (
              <div style={{
                textAlign: 'center', backgroundColor: 'whitesmoke', padding: 10, borderRadius: 20,
              }}
              >
                <Typography variant="body2">
                  Has alcanzado el máximo de reintentos permitido por la aplicación.
                  Si tienes problemas con el acceso, por favor contáctanos:
                </Typography>
                <br />
                <AgentContactButton />
              </div>
            )}
        </>
      )}
    />
  );
}
